import { Scale, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface WeightBannerProps {
  current_weight_grams: number | null;
  weight_source: string | null;
  profile_weight_grams: number | null;
  disabled?: boolean;
  busy?: boolean;
  onCapture: (args: {
    weight_grams: number;
    source: 'pos' | 'manual';
    reason?: string;
  }) => void | Promise<void>;
  onClear: (reason: string) => void | Promise<void>;
}

/**
 * Stage C — POS current-visit weight capture. Doctors type the current measured
 * weight here; backend uses it for weight-based pricing rules and `requires_current_weight`
 * gates. Profile weight (`pets.weight_grams`) is displayed only as muted context — NEVER
 * used by the backend for pricing.
 */
export function WeightBanner({
  current_weight_grams,
  weight_source,
  profile_weight_grams,
  disabled,
  busy,
  onCapture,
  onClear,
}: WeightBannerProps): JSX.Element {
  const { t } = useTranslation();
  const [dialog_open, setDialogOpen] = useState<'capture' | 'edit' | null>(null);

  const display_kg = current_weight_grams !== null ? (current_weight_grams / 1000).toFixed(3) : null;
  const profile_kg =
    profile_weight_grams !== null && profile_weight_grams !== undefined
      ? (profile_weight_grams / 1000).toFixed(3)
      : null;

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-primary" aria-hidden />
          <div className="text-sm">
            {display_kg ? (
              <span className="font-mono font-semibold tabular-nums">
                {display_kg} kg
                {weight_source ? (
                  <Badge variant="muted" className="ml-2 text-xs">
                    {t(`doctor_pos.weight_source_${weight_source}`)}
                  </Badge>
                ) : null}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('doctor_pos.weight_missing')}</span>
            )}
            {profile_kg ? (
              <p className="text-xs text-muted-foreground">
                {t('doctor_pos.weight_profile_hint', { kg: profile_kg })}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {display_kg ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setDialogOpen('edit')}
                disabled={disabled || busy}
              >
                {t('doctor_pos.weight_edit')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt(t('doctor_pos.weight_clear_reason_prompt'));
                  if (reason && reason.trim().length > 0) {
                    void onClear(reason.trim());
                  }
                }}
                disabled={disabled || busy}
                aria-label={t('doctor_pos.weight_clear')}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={() => setDialogOpen('capture')}
              disabled={disabled || busy}
            >
              {t('doctor_pos.weight_capture')}
            </Button>
          )}
        </div>
      </div>
      {dialog_open ? (
        <WeightDialog
          mode={dialog_open}
          initial_grams={current_weight_grams}
          busy={busy}
          onClose={() => setDialogOpen(null)}
          onSubmit={async (args) => {
            await onCapture(args);
            setDialogOpen(null);
          }}
        />
      ) : null}
    </>
  );
}

interface WeightDialogProps {
  mode: 'capture' | 'edit';
  initial_grams: number | null;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (args: { weight_grams: number; source: 'pos' | 'manual'; reason?: string }) => void | Promise<void>;
}

function WeightDialog({ mode, initial_grams, busy, onClose, onSubmit }: WeightDialogProps): JSX.Element {
  const { t } = useTranslation();
  const initial_kg = initial_grams !== null ? (initial_grams / 1000).toFixed(3) : '';
  const [kg, setKg] = useState<string>(initial_kg);
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const can_submit =
    !busy &&
    /^\d+(\.\d{1,3})?$/.test(kg.trim()) &&
    parseFloat(kg) > 0 &&
    (mode === 'capture' || reason.trim().length > 0);

  const onClickSave = async (): Promise<void> => {
    if (!can_submit) return;
    const grams = Math.round(parseFloat(kg) * 1000);
    if (grams <= 0 || grams > 5_000_000) {
      setError(t('doctor_pos.weight_invalid'));
      return;
    }
    setError(null);
    await onSubmit({
      weight_grams: grams,
      source: mode === 'capture' ? 'pos' : 'manual',
      reason: mode === 'edit' ? reason.trim() : undefined,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>
            {mode === 'capture' ? t('doctor_pos.weight_dialog_capture_title') : t('doctor_pos.weight_dialog_edit_title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t('doctor_pos.weight_dialog_description')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="weight-kg">{t('doctor_pos.weight_kg_label')}</Label>
            <Input
              id="weight-kg"
              type="text"
              inputMode="decimal"
              value={kg}
              onChange={(e) => setKg(e.target.value)}
              disabled={busy}
              placeholder="4.500"
            />
          </div>
          {mode === 'edit' ? (
            <div className="space-y-1">
              <Label htmlFor="weight-reason">{t('doctor_pos.weight_reason')}</Label>
              <Input
                id="weight-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                maxLength={255}
                placeholder={t('doctor_pos.weight_reason_placeholder')}
              />
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSave();
            }}
            disabled={!can_submit}
          >
            {busy ? t('common.loading') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
